import React, { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Facebook, CheckCircle, AlertCircle, RefreshCw } from 'lucide-react';
import { toast } from 'sonner';
import { supabase } from '@/integrations/supabase/client';

interface TestResult {
  success: boolean;
  tests?: {
    profile: { success: boolean; status: number; data: any };
    tokenInfo: { success: boolean; status: number; data: any };
    permissions: { success: boolean; status: number; data: any };
  };
  summary?: {
    tokenValid: boolean;
    userDataAccess: boolean;
    emailAccess: boolean;
    permissionsGranted: boolean;
  };
  error?: string;
}

const FacebookAPITester = () => {
  const [testing, setTesting] = useState(false);
  const [result, setResult] = useState<TestResult | null>(null);

  const testFacebookAPI = async () => {
    setTesting(true);
    try {
      console.log('🧪 Testing Facebook API...');
      
      const { data, error } = await supabase.functions.invoke('test-facebook-api', {
        body: {}
      });

      if (error) {
        console.error('❌ Error invoking test function:', error);
        throw new Error(error.message);
      }

      console.log('✅ Test function response:', data);
      setResult(data);

      if (data.success) {
        toast.success('Facebook API test completed!');
      } else {
        toast.error('Facebook API test failed. Check results below.');
      }

    } catch (error: any) {
      console.error('💥 Error testing Facebook API:', error);
      setResult({ success: false, error: error.message });
      toast.error(`Test error: ${error.message}`);
    } finally {
      setTesting(false);
    }
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center space-x-2">
          <Facebook className="h-5 w-5 text-blue-600" />
          <span>Facebook API Tester</span>
        </CardTitle>
        <CardDescription>
          Test Facebook API access using your access token
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="flex items-center justify-between">
          <div className="text-sm text-muted-foreground">
            Tests profile access, permissions, and token validity
          </div>
          <Button 
            onClick={testFacebookAPI} 
            disabled={testing}
            className="flex items-center space-x-2"
          >
            {testing ? (
              <RefreshCw className="h-4 w-4 animate-spin" />
            ) : (
              <Facebook className="h-4 w-4" />
            )}
            <span>{testing ? 'Testing...' : 'Test Facebook API'}</span>
          </Button>
        </div>

        {result && (
          <div className="mt-4 p-4 bg-muted rounded-lg">
            <div className="flex items-center space-x-2 mb-4">
              {result.success ? (
                <CheckCircle className="h-5 w-5 text-green-600" />
              ) : (
                <AlertCircle className="h-5 w-5 text-red-600" />
              )}
              <span className="font-medium">
                {result.success ? 'API Tests Completed' : 'API Tests Failed'}
              </span>
            </div>
            
            {result.error && (
              <div className="text-sm text-red-600 mb-4">
                <strong>Error:</strong> {result.error}
              </div>
            )}

            {result.summary && (
              <div className="space-y-2 mb-4">
                <div className="text-sm font-medium">Test Summary:</div>
                <div className="grid grid-cols-2 gap-2">
                  <div className="flex items-center space-x-2">
                    <Badge variant={result.summary.tokenValid ? "default" : "destructive"}>
                      {result.summary.tokenValid ? "✅" : "❌"} Token Valid
                    </Badge>
                  </div>
                  <div className="flex items-center space-x-2">
                    <Badge variant={result.summary.userDataAccess ? "default" : "destructive"}>
                      {result.summary.userDataAccess ? "✅" : "❌"} User Data
                    </Badge>
                  </div>
                  <div className="flex items-center space-x-2">
                    <Badge variant={result.summary.emailAccess ? "default" : "destructive"}>
                      {result.summary.emailAccess ? "✅" : "❌"} Email Access
                    </Badge>
                  </div>
                  <div className="flex items-center space-x-2">
                    <Badge variant={result.summary.permissionsGranted ? "default" : "destructive"}>
                      {result.summary.permissionsGranted ? "✅" : "❌"} Permissions
                    </Badge>
                  </div>
                </div>
              </div>
            )}

            {result.tests && (
              <div className="space-y-2">
                <div className="text-sm font-medium">Detailed Results:</div>
                <div className="space-y-1 text-xs text-muted-foreground">
                  <div>📋 Profile API: {result.tests.profile.success ? '✅' : '❌'} (Status: {result.tests.profile.status})</div>
                  <div>🔑 Token Info: {result.tests.tokenInfo.success ? '✅' : '❌'} (Status: {result.tests.tokenInfo.status})</div>
                  <div>🛡️ Permissions: {result.tests.permissions.success ? '✅' : '❌'} (Status: {result.tests.permissions.status})</div>
                </div>
              </div>
            )}
          </div>
        )}

        <div className="text-xs text-muted-foreground">
          <p>💡 <strong>Note:</strong> Make sure to add your Facebook access token as a Supabase secret first.</p>
        </div>
      </CardContent>
    </Card>
  );
};

export default FacebookAPITester;